import {
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Switch,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import React, { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import {
  createUserIntegration,
  deleteUserIntegration,
  updateIntegrationDeviceGroups,
  updateIntegrationEmails,
  updateIntegrationNotifications,
} from "../../../actions/integrationActions"
import {
  AlertSettings,
  DigestSettings,
  IntegrationPlatforms,
  NotificationConstants,
} from "../../../util/constants"
import CenteredCircularProgress from "../../Common/CenteredCircularProgress"
import BasicModal from "../../Common/Modals/BasicModal"
import EmailList from "./EmailList"
import GroupsSection from "./GroupsSection"
import NotificationSection from "./NotificationSection"

const styles = (theme) => ({
  listItem: {
    padding: theme.spacing(3),
    margin: 0,
  },
  listItemText: {
    marginLeft: theme.spacing(1),
    fontSize: "1rem",
  },
  integrationSettingsContainer: {
    marginBottom: theme.spacing(3),
  },
  overallContainer: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    margin: 0,
    padding: 0,
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
  },
  checkbox: {
    paddingLeft: 0,
  },
})

const deleteModalContent = (
  <Typography>
    Deleting the integration is permanent. You may add a new integration after deleting the old one.
  </Typography>
)

const IntegrationItem = (props) => {
  const {
    classes,
    icon,
    text,
    platform,
    hasIntegration,
    availableClasses,
    availableGroups,
    loading,
    integrationData,
    snackbarMessageController,
    shouldDisable,
  } = props

  const [apiKey, setApiKey] = useState("")
  const [emails, setEmails] = useState([])
  const [phoneNumbers, setPhoneNumbers] = useState([])
  const [alert, setAlert] = useState(true)
  const [digest, setDigest] = useState(false)
  const [notificationClasses, setNotificationClasses] = useState([])
  const [groupNotifications, setGroupNotifications] = useState([])
  const [processedGroupNotifications, setProcessedGroupNotifications] = useState([])
  const [switchEnabled, setSwitchEnabled] = useState(false)
  const dispatch = useDispatch()

  const getNotificationSection = () => {
    return (
      <>
        <Typography className={classes.title} variant="subtitle1" noWrap>
          Notification Settings
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                onChange={(event) => setSwitchEnabled(event.target.checked)}
                checked={switchEnabled}
              />
            }
            label="Notify for select device groups?"
          />
        </FormGroup>
        {switchEnabled ? (
          <GroupsSection
            availableGroups={availableGroups}
            availableClasses={availableClasses}
            onGroupChange={setProcessedGroupNotifications}
            notificationGroups={groupNotifications}
          />
        ) : (
          <NotificationSection
            notificationClasses={notificationClasses}
            onNotificationChange={setNotificationClasses}
            availableClasses={availableClasses}
          />
        )}
      </>
    )
  }

  const getEmailContent = () => {
    return (
      <div className={classes.overallContainer}>
        <div className={classes.integrationSettingsContainer}>
          <Typography variant="subtitle1">Email Settings</Typography>
          <EmailList
            emails={emails}
            setEmails={(newEmails) => setEmails(newEmails)}
            snackbarMessageController={snackbarMessageController}
          />
          <div className={classes.checkboxContainer}>
            <Checkbox
              className={classes.checkbox}
              checked={alert}
              onChange={() => setAlert(!alert)}
              inputProps={{ "aria-label": "controlled" }}
            />
            <Typography variant="body1">Alert as Insights are Recorded</Typography>
          </div>
          <div className={classes.checkboxContainer}>
            <Checkbox
              className={classes.checkbox}
              checked={digest}
              onChange={() => setDigest(!digest)}
              inputProps={{ "aria-label": "controlled" }}
            />
            <Typography variant="body1">Send Daily Digest Email</Typography>
          </div>
        </div>
        {getNotificationSection()}
      </div>
    )
  }

  let createContent = <Typography></Typography>
  let editContent = <Typography></Typography>
  let platformSpecificFunction

  switch (platform) {
    case IntegrationPlatforms.GUNDI:
      createContent = (
        <div className={classes.overallContainer}>
          <div className={classes.integrationSettingsContainer}>
            <Typography variant="subtitle1">Integration Settings</Typography>
            <TextField
              fullWidth
              label="Gundi API Key"
              margin="dense"
              name="gundiApiKey"
              variant="outlined"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
            />
          </div>
          {getNotificationSection()}
        </div>
      )
      editContent = <div className={classes.overallContainer}>{getNotificationSection()}</div>
      break
    case IntegrationPlatforms.GUNDI_V2:
      createContent = (
        <div className={classes.overallContainer}>
          <div className={classes.integrationSettingsContainer}>
            <Typography variant="subtitle1">Integration Settings</Typography>
            <Typography>
              The V2 API token can not be verified ahead of time. Please double check that the API
              token is correct prior to clicking save.
            </Typography>
            <TextField
              fullWidth
              label="Gundi V2 API Key"
              margin="dense"
              name="gundiV2ApiKey"
              variant="outlined"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
            />
          </div>
          {getNotificationSection()}
        </div>
      )
      editContent = <div className={classes.overallContainer}>{getNotificationSection()}</div>
      break
    case IntegrationPlatforms.EMAIL:
      createContent = getEmailContent()
      editContent = getEmailContent()
      platformSpecificFunction = () =>
        updateIntegrationEmails({
          options: {
            emails,
            alertSetting: alert ? AlertSettings.ENABLED : AlertSettings.DISABLED,
            digestSetting: digest ? DigestSettings.DAILY : null,
          },
        })
      break
  }

  const clear = useCallback(() => {
    setEmails([])
    setPhoneNumbers([])
    setAlert(true)
    setDigest(false)
    setNotificationClasses([])
    setGroupNotifications([])
    setApiKey("")
  }, [])

  // biome-ignore lint: Adding other dependencies causes infinite loops
  const refresh = useCallback(() => {
    if (integrationData) {
      setEmails(integrationData.emails ? integrationData.emails : emails)
      setAlert(
        integrationData.alertSetting
          ? integrationData.alertSetting === AlertSettings.ENABLED
          : alert,
      )
      setDigest(
        integrationData.digestSetting
          ? integrationData.digestSetting === DigestSettings.DAILY
          : digest,
      )
      setPhoneNumbers(integrationData.phoneNumbers ? integrationData.phoneNumbers : phoneNumbers)
      setNotificationClasses(
        integrationData.notificationClasses
          ? integrationData.notificationClasses
          : notificationClasses,
      )
      setGroupNotifications(
        integrationData.groupNotifications
          ? integrationData.groupNotifications
          : groupNotifications,
      )
      setSwitchEnabled(
        integrationData.groupNotifications && integrationData.groupNotifications.length > 0,
      )
      setApiKey(integrationData.gundiApiKey ? integrationData.gundiApiKey : apiKey)
    } else {
      clear()
    }
  }, [integrationData, clear])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleSubmit = () => {
    dispatch(
      createUserIntegration({
        platform,
        options: {
          gundiApiKey: apiKey,
          gundiV2ApiKey: apiKey,
          emails,
          phoneNumbers,
          alertSetting: alert ? AlertSettings.ENABLED : AlertSettings.DISABLED,
          digestSetting: digest ? DigestSettings.DAILY : null,
          groupNotifications:
            processedGroupNotifications.length === 0 || !switchEnabled
              ? []
              : processedGroupNotifications,
        },
        notificationClasses: switchEnabled
          ? [NotificationConstants.ALL]
          : notificationClasses.length === 0
            ? [NotificationConstants.NONE]
            : notificationClasses,
      }),
    )
  }

  const handleEdit = () => {
    if (platformSpecificFunction) {
      dispatch(platformSpecificFunction())
    }
    dispatch(
      updateIntegrationNotifications(platform, {
        notificationClasses: switchEnabled
          ? [NotificationConstants.ALL]
          : notificationClasses.length === 0
            ? [NotificationConstants.NONE]
            : notificationClasses,
      }),
    )
    dispatch(
      updateIntegrationDeviceGroups(platform, {
        groupNotifications:
          processedGroupNotifications.length === 0 || !switchEnabled
            ? []
            : processedGroupNotifications,
      }),
    )
  }

  const handleDelete = () => {
    dispatch(deleteUserIntegration(platform))
    clear()
  }

  return (
    <ListItem className={classes.listItem} disabled={shouldDisable}>
      <ListItemAvatar>
        <Avatar>{icon}</Avatar>
      </ListItemAvatar>
      <ListItemText primary={text} classes={{ primary: classes.listItemText }} />
      {loading ? (
        <CenteredCircularProgress size={30} />
      ) : hasIntegration ? (
        <div>
          <BasicModal
            confirmText="Edit"
            title={`Edit ${text} Integration?`}
            content={editContent}
            icon={<EditIcon />}
            confirmAction={handleEdit}
            fullWidth
            cancelAction={refresh}
            shouldDisable={shouldDisable}
          />
          <BasicModal
            confirmText="Delete"
            title={`Delete ${text} Integration?`}
            content={deleteModalContent}
            icon={<DeleteIcon />}
            fullWidth
            confirmAction={handleDelete}
            shouldDisable={shouldDisable}
          />
        </div>
      ) : (
        <BasicModal
          confirmText="Save"
          title={`${text} Integration`}
          icon={<AddIcon />}
          content={createContent}
          confirmAction={handleSubmit}
          fullWidth
          cancelAction={refresh}
          shouldDisable={shouldDisable}
        />
      )}
    </ListItem>
  )
}

export default withStyles(styles)(IntegrationItem)
